import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { inputCls } from "@/admin/fields";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLES = [
  { value: "super_admin", label: "Super Admin — full access" },
  { value: "content_admin", label: "Content Admin — pages, gallery, news" },
  { value: "club_admin", label: "Club Admin — coaches, staff, sponsors, enquiries" },
];

export default function UsersManager() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/users"); setUsers(data); } catch { toast.error("Access denied"); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/users/${editing.id}`, { name: editing.name, role: editing.role, password: editing.password || undefined });
      else await api.post("/admin/users", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this admin user?")) return; try { await api.delete(`/admin/users/${id}`); load(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-extrabold uppercase text-white">Admin Users</h1><p className="text-sm text-slate-400">Manage administrator accounts and roles.</p></div>
        <button onClick={() => setEditing({ email: "", name: "", role: "club_admin", password: "" })} data-testid="user-add-button" className="inline-flex items-center gap-2 rounded-lg bg-[#E8A317] px-4 py-2 text-sm font-bold uppercase text-[#0A1626]"><Plus className="h-4 w-4" /> Add User</button>
      </div>
      {loading ? <Loader2 className="mx-auto mt-16 h-7 w-7 animate-spin text-slate-400" /> : (
        <div className="mt-6 overflow-hidden rounded-xl border border-slate-700">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#0C131F] text-xs uppercase text-slate-400"><tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3 text-right">Actions</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-700/60" data-testid={`user-row-${u.id}`}>
                  <td className="p-3 font-semibold text-white">{u.name}</td>
                  <td className="p-3 text-slate-300">{u.email}</td>
                  <td className="p-3"><span className="rounded bg-[#1E63D6]/30 px-2 py-0.5 text-xs font-bold uppercase text-[#6ba4ff]">{u.role?.replace("_", " ")}</span></td>
                  <td className="p-3"><div className="flex justify-end gap-1"><button onClick={() => setEditing({ ...u, password: "" })} className="rounded bg-slate-700 px-3 py-1.5 text-xs font-bold hover:bg-[#1E63D6]">Edit</button>{u.email !== user?.email && <button onClick={() => remove(u.id)} className="rounded bg-slate-700 p-1.5 hover:bg-red-600"><Trash2 className="h-4 w-4" /></button>}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md border-slate-700 bg-[#151F30] text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">{editing?.id ? "Edit" : "Add"} Admin User</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Name</label><input className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="user-name" /></div>
              {!editing.id && <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Email</label><input type="email" className={inputCls} value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="user-email" /></div>}
              <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Role</label>
                <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger className={inputCls} data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">{editing.id ? "New Password (leave blank to keep)" : "Password"}</label><input type="password" className={inputCls} value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} data-testid="user-password" /></div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-300">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="user-save-button" className="inline-flex items-center gap-2 rounded-lg bg-[#E8A317] px-5 py-2 text-sm font-bold uppercase text-[#0A1626]">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
